import React from "react";
import { Link } from "react-router-dom";
import { formatValue } from "@/lib/api";
import { Loader2, AlertTriangle, ArrowRightLeft } from "lucide-react";
import { POS_COLORS } from "./shared";

export default function CrunchTab({ crunch }) {
  if (crunch === null) {
    return <div className="card-bordered p-12 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>;
  }
  if (crunch.owned === false) {
    return (
      <div className="card-bordered p-8 text-center text-sm text-[var(--text-3)]" data-testid="crunch-unowned">
        Roster Crunch works on a league where you own a team. Connect the Sleeper account that owns a roster in this league.
      </div>
    );
  }
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="card-bordered p-4" data-testid="crunch-overby">
          <div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">ROSTER</div>
          <div className="font-mono text-2xl" style={{ color: crunch.over_by > 0 ? "var(--negative)" : "var(--accent)" }}>{crunch.active_count}/{crunch.roster_limit}</div>
          <div className="text-[10px] text-[var(--text-3)]">{crunch.over_by > 0 ? `${crunch.over_by} over the limit` : "Within limit"}</div>
        </div>
        <div className="card-bordered p-4">
          <div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">{crunch.over_by > 0 ? "VALUE AT RISK" : "LOWEST-VALUE DEPTH"}</div>
          <div className="font-mono text-2xl" style={{ color: crunch.over_by > 0 ? "var(--amber)" : "var(--text-2)" }}>{formatValue(crunch.over_by > 0 ? crunch.value_at_risk : (crunch.cut_candidates[0]?.value || 0))}</div>
          <div className="text-[10px] text-[var(--text-3)]">{crunch.over_by > 0 ? `value lost if you don't trade your ${crunch.over_by} excess piece${crunch.over_by > 1 ? "s" : ""}` : "no forced cuts — your weakest bench piece to shop"}</div>
        </div>
        <div className="card-bordered p-4">
          <div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">BIGGEST NEED</div>
          <div className="font-mono text-2xl" style={{ color: POS_COLORS[crunch.need_position] }}>{crunch.need_position}</div>
          <div className="text-[10px] text-[var(--text-3)]">thinnest position</div>
        </div>
        <div className="card-bordered p-4">
          <div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">SURPLUS</div>
          <div className="font-mono text-2xl" style={{ color: POS_COLORS[crunch.surplus_position] }}>{crunch.surplus_position}</div>
          <div className="text-[10px] text-[var(--text-3)]">trade from depth</div>
        </div>
      </div>

      <div className="card-bordered overflow-hidden mb-4" data-testid="crunch-candidates">
        <div className="px-4 py-3 border-b border-[var(--border)] text-[10px] tracking-[0.2em] text-[var(--text-3)] font-mono flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-[var(--amber)]" /> {crunch.over_by > 0 ? "FORCED CUT CANDIDATES" : "POTENTIAL CUT CANDIDATES"}
        </div>
        {crunch.cut_candidates.length === 0 ? (
          <div className="p-5 text-sm text-[var(--text-3)]">No forced cuts needed — your bench is lean and holds value. Nothing on the chopping block right now.</div>
        ) : crunch.cut_candidates.map(p => (
          <div key={p.player_id} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center text-sm border-b border-[var(--border)] last:border-0 hover:bg-white/5" data-testid={`cut-${p.player_id}`}>
            <div className="col-span-5 min-w-0">
              <Link to={`/app/players/${p.player_id}`} className="hover:text-[var(--accent)] truncate block">{p.name}</Link>
              <div className="text-[10px] tracking-widest text-[var(--text-3)]">{p.position} · {p.team}{p.age ? ` · ${p.age}y` : ""}{p.injury ? ` · ${p.injury}` : ""}</div>
            </div>
            <div className="col-span-3">
              <div className="h-1.5 rounded-sm bg-[var(--elev)] overflow-hidden">
                <div style={{ width: `${p.drop_score}%`, background: p.drop_score >= 66 ? "var(--negative)" : p.drop_score >= 40 ? "var(--amber)" : "var(--text-3)" }} className="h-full" />
              </div>
              <div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono mt-1">DROP SCORE {p.drop_score}</div>
            </div>
            <div className="col-span-2 text-right font-mono text-[var(--text-2)]">{formatValue(p.value)}</div>
            <div className="col-span-2 text-right font-mono text-xs text-[var(--text-3)]">{p.week1_proj} pj</div>
          </div>
        ))}
      </div>

      <div className="card-bordered overflow-hidden" data-testid="crunch-packages">
        <div className="px-4 py-3 border-b border-[var(--border)] text-[10px] tracking-[0.2em] text-[var(--text-3)] font-mono flex items-center gap-2">
          <ArrowRightLeft className="w-3.5 h-3.5 text-[var(--accent)]" /> AUTOMATED 2-FOR-1 UPGRADES
        </div>
        {crunch.packages.length === 0 ? (
          <div className="p-5 text-sm text-[var(--text-3)]">No clean 2-for-1 upgrade found right now. Your depth pieces don't line up with another team's surplus at {crunch.need_position}.</div>
        ) : crunch.packages.map((pk, i) => (
          <div key={`${pk.target_manager}-${pk.get?.name}-${i}`} className="p-4 border-b border-[var(--border)] last:border-0" data-testid={`pkg-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">To {pk.target_manager}{pk.open_spots > 0 ? <span className="text-[10px] text-[var(--accent)] ml-2 font-mono">{pk.open_spots} OPEN SPOT{pk.open_spots > 1 ? "S" : ""}</span> : null}</div>
              <div className="text-[10px] font-mono px-2 py-0.5 rounded-sm border border-[var(--border)] text-[var(--accent)]">{pk.fairness}% FAIR</div>
            </div>
            <div className="flex items-center gap-2 text-sm flex-wrap">
              <div className="flex flex-col gap-1">
                {pk.send.map((s, j) => <span key={`${s.name}-${j}`} className="text-[var(--negative)]">↑ {s.name} <span className="text-[var(--text-3)] text-xs">{s.position} {formatValue(s.value)}</span></span>)}
              </div>
              <ArrowRightLeft className="w-4 h-4 text-[var(--text-3)] mx-1" />
              <span className="text-[var(--accent)]">↓ {pk.get.name} <span className="text-[var(--text-3)] text-xs">{pk.get.position} {formatValue(pk.get.value)}</span></span>
            </div>
            <p className="text-xs text-[var(--text-3)] mt-2 italic">{pk.proposal}</p>
          </div>
        ))}
      </div>
    </>
  );
}
